import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { getProducts, getCategories } from "../lib/api";
import ProductCard from "../components/ProductCard";
import Reveal from "../components/Reveal";

const SIZES = ["S", "M", "L", "XL"];
const COLORS = [
  { name: "Noir", hex: "#000000" }, { name: "Doré", hex: "#C9A84C" }, { name: "Rose", hex: "#F2C4CE" },
  { name: "Bleu", hex: "#6b8cbe" }, { name: "Fleuri", hex: "#c98fb0" }, { name: "Or rosé", hex: "#e0bfb8" },
];
const SORTS = [
  { v: "new", l: "Nouveautés" }, { v: "price_asc", l: "Prix croissant" },
  { v: "price_desc", l: "Prix décroissant" }, { v: "bestseller", l: "Best-sellers" },
];

const TITLES = {
  default: { t: "La Boutique", s: "Toute notre sélection mode" },
  new: { t: "Nouveautés", s: "Les dernières pièces arrivées" },
  promo: { t: "Promotions", s: "Nos offres et réductions du moment" },
};

export default function Shop({ mode = "default" }) {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileFilters, setMobileFilters] = useState(false);

  const category = params.get("category") || "all";
  const search = params.get("search") || "";
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [maxPrice, setMaxPrice] = useState(100);
  const [sort, setSort] = useState("new");

  useEffect(() => { getCategories().then(setCats).catch(() => {}); }, []);

  useEffect(() => {
    setLoading(true);
    const q = { sort };
    if (category !== "all") q.category = category;
    if (search) q.search = search;
    if (size) q.size = size;
    if (color) q.color = color;
    if (maxPrice < 100) q.max_price = maxPrice;
    if (mode === "new") q.is_new = true;
    if (mode === "promo") q.is_promo = true;
    getProducts(q).then((d) => { setProducts(d); setLoading(false); }).catch(() => setLoading(false));
  }, [category, search, size, color, maxPrice, sort, mode]);

  const meta = TITLES[mode] || TITLES.default;

  const setCategory = (slug) => {
    const p = new URLSearchParams(params);
    if (slug === "all") p.delete("category"); else p.set("category", slug);
    p.delete("search");
    setParams(p);
  };

  const FilterPanel = () => (
    <div className="space-y-8">
      <div>
        <h4 className="font-display text-lg text-encre mb-3">Catégories</h4>
        <ul className="space-y-1">
          <li><button onClick={() => setCategory("all")} className={`text-sm py-1 transition-colors ${category === "all" ? "text-dore font-medium" : "text-ardoise hover:text-encre"}`}>Toute la boutique</button></li>
          {cats.map((c) => (
            <li key={c.slug}><button onClick={() => setCategory(c.slug)} data-testid={`filter-cat-${c.slug}`} className={`text-sm py-1 transition-colors ${category === c.slug ? "text-dore font-medium" : "text-ardoise hover:text-encre"}`}>{c.name}</button></li>
          ))}
        </ul>
      </div>
      <div>
        <h4 className="font-display text-lg text-encre mb-3">Prix max : {maxPrice}€</h4>
        <input type="range" min="15" max="100" value={maxPrice} onChange={(e) => setMaxPrice(+e.target.value)} className="w-full accent-dore" data-testid="price-slider" />
      </div>
      <div>
        <h4 className="font-display text-lg text-encre mb-3">Taille</h4>
        <div className="flex flex-wrap gap-2">
          {SIZES.map((s) => (
            <button key={s} onClick={() => setSize(size === s ? "" : s)} data-testid={`filter-size-${s}`}
              className={`w-10 h-10 border text-sm transition-colors ${size === s ? "bg-noir text-white border-noir" : "border-dore/30 text-encre hover:border-dore"}`}>{s}</button>
          ))}
        </div>
      </div>
      <div>
        <h4 className="font-display text-lg text-encre mb-3">Couleur</h4>
        <div className="flex flex-wrap gap-3">
          {COLORS.map((c) => (
            <button key={c.name} onClick={() => setColor(color === c.name ? "" : c.name)} title={c.name} data-testid={`filter-color-${c.name}`}
              className={`w-8 h-8 rounded-full border-2 transition-all ${color === c.name ? "border-dore scale-110" : "border-white shadow"}`} style={{ background: c.hex }} />
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div data-testid="shop-page">
      <div className="bg-noir text-center py-16 px-6 grain relative">
        <h1 className="font-display text-4xl md:text-6xl text-white relative">{meta.t}</h1>
        <p className="font-serif-el italic text-dore text-lg mt-2 relative">{search ? `Résultats pour "${search}"` : meta.s}</p>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12 grid lg:grid-cols-[240px_1fr] gap-10">
        <aside className="hidden lg:block"><FilterPanel /></aside>

        <div>
          <div className="flex items-center justify-between mb-8">
            <button onClick={() => setMobileFilters(true)} className="lg:hidden flex items-center gap-2 text-sm border border-dore/30 px-4 py-2" data-testid="mobile-filter-open"><SlidersHorizontal size={16} /> Filtres</button>
            <p className="text-sm text-ardoise hidden sm:block">{products.length} article{products.length > 1 ? "s" : ""}</p>
            <select value={sort} onChange={(e) => setSort(e.target.value)} className="border border-dore/30 px-4 py-2 text-sm bg-white outline-none" data-testid="sort-select">
              {SORTS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {Array.from({ length: 6 }).map((_, i) => <div key={i} className="aspect-[3/4] bg-white animate-pulse" />)}
            </div>
          ) : products.length === 0 ? (
            <p className="text-center text-ardoise py-20">Aucun produit ne correspond à votre recherche.</p>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-x-6 md:gap-y-10" data-testid="products-grid">
              {products.map((p, i) => <Reveal key={p.slug} delay={(i % 3) * 0.05}><ProductCard product={p} /></Reveal>)}
            </div>
          )}
        </div>
      </div>

      {mobileFilters && (
        <div className="fixed inset-0 z-[70] lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileFilters(false)} />
          <div className="absolute right-0 top-0 h-full w-4/5 max-w-sm bg-creme p-6 overflow-y-auto">
            <div className="flex justify-between mb-6"><span className="font-display text-xl">Filtres</span><button onClick={() => setMobileFilters(false)}><X size={22} /></button></div>
            <FilterPanel />
            <button onClick={() => setMobileFilters(false)} className="w-full mt-8 bg-noir text-white py-3 text-xs uppercase tracking-widest">Voir les résultats</button>
          </div>
        </div>
      )}
    </div>
  );
}
